import { useCallback, useEffect, useRef, useState } from 'react';
import { refreshBilling } from '../../services/billing/billingApi';
import { subscribeAppResume } from '../../utils/ui/appResumeGate';
import { isSubscriptionLive } from '../../utils/billing/billingState';

export const CHECKOUT_POLL_MS = 3000;
/** KAN-1044 back-off: 3s for the first 15s, 5s until 30s, then 10s (the webhook ping usually wins long before). */
export function checkoutPollDelay(elapsedMs) {
  if (elapsedMs < 15000) return CHECKOUT_POLL_MS;
  if (elapsedMs < 30000) return 5000;
  return 10000;
}
export const CHECKOUT_POLL_MAX_MS = 60000;
/** Round 3: the wait modal says "still confirming" after 20s; polling continues to CHECKOUT_POLL_MAX_MS. */
export const CHECKOUT_SLOW_MS = 20000;
/** Round 3 fix pass: after the modal closes (or the 60s wait ends) we keep asking the server, quietly. */
export const CHECKOUT_BG_POLL_MS = 10000;
export const CHECKOUT_BG_CAP_MS = 180000;
const OPEN = ['activating', 'slow', 'activated'];

/** Reads and clears `?checkout=` without the router (leaf-safe). Returns 'done' | 'cancelled' | null. */
export function takeCheckoutParam() {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const v = url.searchParams.get('checkout');
  if (!v) return null;
  url.searchParams.delete('checkout');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return v === 'done' ? 'done' : v === 'cancelled' ? 'cancelled' : null;
}

/**
 * KAN-196 — returning from Stripe Checkout lands on /app/company/plan?checkout=done.
 * Shows the wait modal and asks the server to re-read the subscription with a back-off (3s, 5s, then 10s; KAN-1044) up to 60s until it is active/trialing. At 20s the phase becomes 'slow' (a calmer line + Close /
 * "Check again") while polling carries on to 60s. The webhook usually wins first: any live `status` stops the poll.
 * The param is cleared at once. `?checkout=cancelled` just reports `cancelled`.
 *
 * Once live the phase is 'activated' until `dismiss()` (the "You're all set" moment, KAN-852).
 *
 * Background confirm: when the modal is closed, or the 60s wait ends still unconfirmed, the server re-fetch
 * keeps running every 10s (and once on focus / visibility return) until 3 minutes after the return. The modal never
 * re-opens: success flips `lateConfirmed` after a non-fatal reload and the host shows a toast (`ackLate` clears it).
 *
 * @returns {{ phase: null|'activating'|'slow'|'activated'|'cancelled', checkAgain: () => void, dismiss: () => void, lateConfirmed: boolean, ackLate: () => void }}
 */
export default function useCheckoutReturn({ status, reload, onActivated }) {
  const [phase, setPhase] = useState(null);
  // The URL param is consumed exactly once (it is stripped), but the poll must be able to RESTART:
  // StrictMode runs mount -> cleanup -> mount, and the cleanup kills the timers.
  const pending = useRef(undefined);
  const timer = useRef(null);
  const deadline = useRef(0);
  const waitStart = useRef(0);
  const alive = useRef(true);
  const slowTimer = useRef(null);
  const liveRef = useRef(false);
  liveRef.current = isSubscriptionLive(status?.stripeStatus);
  const cbRef = useRef({ reload, onActivated });
  cbRef.current = { reload, onActivated };
  const [lateConfirmed, setLateConfirmed] = useState(false);
  const phaseRef = useRef(null);
  phaseRef.current = phase;
  const startedAt = useRef(0);
  const bgTimer = useRef(null);
  const bgOn = useRef(false); // background polling wanted
  const windowOpen = useRef(false); // a checkout is pending: from the return until live or the 3-minute cap
  const inflight = useRef(false);

  const stop = useCallback(() => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } }, []);
  const stopSlow = useCallback(() => { if (slowTimer.current) { clearTimeout(slowTimer.current); slowTimer.current = null; } }, []);
  const armSlow = useCallback(() => {
    stopSlow();
    slowTimer.current = setTimeout(() => { slowTimer.current = null; if (alive.current) setPhase((p) => (p === 'activating' ? 'slow' : p)); }, CHECKOUT_SLOW_MS);
  }, [stopSlow]);

  const stopBg = useCallback(() => { bgOn.current = false; if (bgTimer.current) { clearTimeout(bgTimer.current); bgTimer.current = null; } }, []);

  /** One server re-fetch for the background / focus path. Returns true once the subscription is live. */
  const probe = useCallback(async () => {
    if (!alive.current || !windowOpen.current || inflight.current) return false;
    if (Date.now() >= startedAt.current + CHECKOUT_BG_CAP_MS) { windowOpen.current = false; stopBg(); return false; }
    inflight.current = true;
    let live = liveRef.current;
    try {
      if (!live) {
        const res = await refreshBilling();
        live = isSubscriptionLive(res?.stripe_status);
      }
    } catch (_e) { /* keep trying until the cap */ }
    inflight.current = false;
    if (!alive.current || !live || !windowOpen.current) return false;
    windowOpen.current = false;
    stopBg();
    pending.current = null;
    try { await cbRef.current.reload?.(); } catch (_e) { /* non-fatal */ }
    if (!alive.current) return true;
    // A modal still open reaches "You're all set" through the status effect; a closed one gets the toast.
    if (!OPEN.includes(phaseRef.current)) setLateConfirmed(true);
    return true;
  }, [stopBg]);

  const startBg = useCallback(() => {
    if (!windowOpen.current || bgOn.current) return;
    bgOn.current = true;
    const loop = async () => {
      bgTimer.current = null;
      if (!bgOn.current || !alive.current) return;
      const done = await probe();
      if (done || !bgOn.current || !windowOpen.current) return;
      bgTimer.current = setTimeout(loop, CHECKOUT_BG_POLL_MS);
    };
    bgTimer.current = setTimeout(loop, CHECKOUT_BG_POLL_MS);
  }, [probe]);

  const tick = useCallback(async () => {
    stop();
    if (!alive.current) return;
    let live = liveRef.current;
    if (!live) {
      try {
        const res = await refreshBilling();
        live = isSubscriptionLive(res?.stripe_status);
        // KAN-1044: ONE authoritative read per tick (refreshBilling); the status is reloaded only once it is live.
        if (live) await cbRef.current.reload?.();
      } catch (_e) { /* keep polling until the deadline */ }
    }
    if (!alive.current) return;
    if (live || liveRef.current) {
      pending.current = null;
      windowOpen.current = false;
      stopBg();
      stopSlow();
      setPhase('activated');
      cbRef.current.onActivated?.();
      return;
    }
    if (Date.now() >= deadline.current) { pending.current = null; setPhase('slow'); startBg(); return; }
    timer.current = setTimeout(tick, checkoutPollDelay(Date.now() - waitStart.current));
  }, [stop, stopSlow, stopBg, startBg]);

  useEffect(() => {
    alive.current = true;
    if (pending.current === undefined) pending.current = takeCheckoutParam();
    if (pending.current === 'cancelled') { setPhase('cancelled'); pending.current = null; }
    if (pending.current === 'done') {
      if (!deadline.current) { deadline.current = Date.now() + CHECKOUT_POLL_MAX_MS; waitStart.current = Date.now(); }
      if (!startedAt.current) startedAt.current = Date.now();
      windowOpen.current = true;
      setPhase('activating');
      armSlow();
      void tick();
    }
    // While a checkout is pending, coming back to the tab asks the server once.
    const stopResume = subscribeAppResume(() => { void probe(); }); // shared 60s-away gate (KAN-1044)
    return () => {
      alive.current = false; stop(); stopSlow(); stopBg(); inflight.current = false;
      stopResume();
    };
  }, [tick, stop, armSlow, stopSlow, stopBg, probe]);

  // A ping (webhook) made it live while we were polling.
  useEffect(() => {
    if ((phase === 'activating' || phase === 'slow') && isSubscriptionLive(status?.stripeStatus)) {
      stop();
      stopSlow();
      stopBg();
      windowOpen.current = false;
      pending.current = null;
      setPhase('activated');
      cbRef.current.onActivated?.();
    }
  }, [status?.stripeStatus, phase, stop, stopSlow, stopBg]);

  const checkAgain = useCallback(() => {
    pending.current = 'done';
    stopBg();
    windowOpen.current = Date.now() < startedAt.current + CHECKOUT_BG_CAP_MS || !startedAt.current;
    if (!startedAt.current) startedAt.current = Date.now();
    deadline.current = Date.now() + CHECKOUT_POLL_MAX_MS;
    waitStart.current = Date.now();
    setPhase('activating');
    armSlow();
    void tick();
  }, [tick, armSlow, stopBg]);
  // Closing the modal ends the foreground wait only: while still unconfirmed the server re-fetch carries on quietly
  // (hidden: phase is null, so the modal can never re-open) until the 3-minute cap.
  const dismiss = useCallback(() => {
    const unconfirmed = OPEN.includes(phaseRef.current) && phaseRef.current !== 'activated' && !liveRef.current;
    stop(); stopSlow(); pending.current = null; setPhase(null);
    if (unconfirmed) startBg(); else { windowOpen.current = false; stopBg(); }
  }, [stop, stopSlow, startBg, stopBg]);
  const ackLate = useCallback(() => setLateConfirmed(false), []);
  return { phase, checkAgain, dismiss, lateConfirmed, ackLate };
}
